import { inject, Injectable, signal } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from '@angular/fire/firestore';
import { AttendanceEntry, AttendanceEntryStatus, Timesheet, TimesheetStatus } from '../models';
import { AuthStore } from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class AttendanceService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _todayEntry = signal<AttendanceEntry | null>(null);
  private readonly _weekEntries = signal<AttendanceEntry[]>([]);
  private readonly _timesheets = signal<Timesheet[]>([]);
  private readonly _pendingApprovals = signal<Timesheet[]>([]);
  private readonly _loading = signal(false);

  readonly todayEntry = this._todayEntry.asReadonly();
  readonly weekEntries = this._weekEntries.asReadonly();
  readonly timesheets = this._timesheets.asReadonly();
  readonly pendingApprovals = this._pendingApprovals.asReadonly();
  readonly loading = this._loading.asReadonly();

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) throw new Error('No tenant selected');
    return id;
  }

  private get userId(): string {
    const id = this.authStore.userId();
    if (!id) throw new Error('No user logged in');
    return id;
  }

  private get userEmail(): string {
    const email = this.authStore.user()?.email;
    if (!email) throw new Error('No user email');
    return email.toLowerCase();
  }

  async loadTodayEntry(): Promise<void> {
    const today = this.startOfDay(new Date());
    this._loading.set(true);
    try {
      const entry = await this.getEntryForDate(today);
      this._todayEntry.set(entry);
    } finally {
      this._loading.set(false);
    }
  }

  async loadWeekEntries(weekStart: Date): Promise<void> {
    const start = this.startOfDay(weekStart);
    const end = this.startOfDay(this.addDays(start, 6));
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/attendanceEntries`);
      const q = query(
        ref,
        where('userId', '==', this.userId),
        where('date', '>=', start),
        where('date', '<=', end),
        orderBy('date', 'asc')
      );
      const snap = await getDocs(q);
      this._weekEntries.set(snap.docs.map((d) => this.mapEntry(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async loadMyTimesheets(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/timesheets`);
      const q = query(
        ref,
        where('userId', '==', this.userId),
        orderBy('weekStartDate', 'desc')
      );
      const snap = await getDocs(q);
      this._timesheets.set(snap.docs.map((d) => this.mapTimesheet(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async loadPendingApprovals(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/timesheets`);
      const role = this.authStore.userRole();

      // HR admins see all submitted timesheets
      if (role === 'hr_admin') {
        const q = query(ref, where('status', '==', 'submitted'), orderBy('weekStartDate', 'desc'));
        const snap = await getDocs(q);
        this._pendingApprovals.set(snap.docs.map((d) => this.mapTimesheet(d.id, d.data())));
        return;
      }

      // Managers see timesheets assigned to them + unassigned ones from their direct reports
      const approverEmployeeId = await this.getEmployeeIdForUser();
      if (!approverEmployeeId) {
        this._pendingApprovals.set([]);
        return;
      }

      // Query for directly assigned timesheets
      const directQuery = query(
        ref,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', approverEmployeeId),
        orderBy('weekStartDate', 'desc')
      );

      // Query for unassigned timesheets (null or empty approverEmployeeId)
      const unassignedNullQuery = query(
        ref,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', null),
        orderBy('weekStartDate', 'desc')
      );
      const unassignedEmptyQuery = query(
        ref,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', ''),
        orderBy('weekStartDate', 'desc')
      );

      const [directSnap, unassignedNullSnap, unassignedEmptySnap] = await Promise.all([
        getDocs(directQuery),
        getDocs(unassignedNullQuery),
        getDocs(unassignedEmptyQuery),
      ]);

      const directTimesheets = directSnap.docs.map((d) => this.mapTimesheet(d.id, d.data()));

      // For unassigned timesheets, check if the submitter is a direct report
      const unassignedDocs = [...unassignedNullSnap.docs, ...unassignedEmptySnap.docs];
      const backfilledTimesheets = await this.backfillTimesheetsForManager(
        unassignedDocs,
        approverEmployeeId
      );

      // Combine and dedupe
      const allTimesheets = [...directTimesheets, ...backfilledTimesheets];
      const uniqueTimesheets = allTimesheets.filter(
        (sheet, index, self) => self.findIndex((s) => s.id === sheet.id) === index
      );

      // If still no results, try reconciliation
      if (uniqueTimesheets.length === 0) {
        const reconciled = await this.reconcileTimesheetsForManager(approverEmployeeId);
        this._pendingApprovals.set(reconciled);
        return;
      }

      this._pendingApprovals.set(
        uniqueTimesheets.sort((a, b) => b.weekStartDate.getTime() - a.weekStartDate.getTime())
      );
    } finally {
      this._loading.set(false);
    }
  }

  private async backfillTimesheetsForManager(
    docs: Array<{ id: string; data: () => Record<string, unknown>; ref: import('@angular/fire/firestore').DocumentReference }>,
    managerEmployeeId: string
  ): Promise<Timesheet[]> {
    const cache = new Map<string, { managerId: string | null } | null>();
    const matched: Timesheet[] = [];
    const batch = writeBatch(this.firestore);
    let hasUpdates = false;

    for (const docSnap of docs) {
      const data = docSnap.data();
      const userId = data['userId'] as string | undefined;
      if (!userId) continue;

      let record = cache.get(userId);
      if (record === undefined) {
        record = await this.getEmployeeRecordForUserId(userId);
        cache.set(userId, record);
      }

      if (record?.managerId === managerEmployeeId) {
        matched.push(this.mapTimesheet(docSnap.id, data));
        batch.update(docSnap.ref, {
          approverEmployeeId: managerEmployeeId,
          updatedAt: serverTimestamp(),
        });
        hasUpdates = true;
      }
    }

    if (hasUpdates) {
      await batch.commit();
    }

    return matched;
  }

  private async reconcileTimesheetsForManager(managerEmployeeId: string): Promise<Timesheet[]> {
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/timesheets`);
      const snap = await getDocs(
        query(ref, where('status', '==', 'submitted'), orderBy('weekStartDate', 'desc'))
      );
      if (snap.empty) return [];

      const cache = new Map<string, { managerId: string | null } | null>();
      const matched: Timesheet[] = [];
      const batch = writeBatch(this.firestore);
      let hasUpdates = false;

      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        const userId = data['userId'] as string | undefined;
        if (!userId) continue;

        let record = cache.get(userId);
        if (record === undefined) {
          record = await this.getEmployeeRecordForUserId(userId);
          cache.set(userId, record);
        }

        const currentApprover = (data['approverEmployeeId'] as string | null) ?? null;
        if (record?.managerId === managerEmployeeId && currentApprover !== managerEmployeeId) {
          matched.push(this.mapTimesheet(docSnap.id, data));
          batch.update(docSnap.ref, {
            approverEmployeeId: managerEmployeeId,
            updatedAt: serverTimestamp(),
          });
          hasUpdates = true;
        }
      }

      if (hasUpdates) {
        await batch.commit();
      }

      return matched;
    } catch (error) {
      console.error('Failed to reconcile timesheets for manager:', error);
      return [];
    }
  }

  private async getEmployeeRecordForUserId(
    userId: string
  ): Promise<{ managerId: string | null } | null> {
    try {
      const userRef = doc(this.firestore, `tenants/${this.tenantId}/users/${userId}`);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return null;

      const rawEmail = (userSnap.data()['email'] as string | undefined) ?? '';
      if (!rawEmail) return null;
      const normalizedEmail = rawEmail.trim().toLowerCase();

      const employeesRef = collection(this.firestore, `tenants/${this.tenantId}/employees`);
      let employeeSnap = await getDocs(
        query(employeesRef, where('email', '==', normalizedEmail))
      );
      if (!employeeSnap.docs[0] && rawEmail !== normalizedEmail) {
        employeeSnap = await getDocs(query(employeesRef, where('email', '==', rawEmail)));
      }
      const employeeDoc = employeeSnap.docs[0];
      if (!employeeDoc) return null;
      const data = employeeDoc.data();
      return { managerId: (data['managerId'] as string | null) ?? null };
    } catch (error) {
      console.error('Failed to resolve employee for user:', error);
      return null;
    }
  }

  async clockIn(): Promise<void> {
    const now = new Date();
    const today = this.startOfDay(now);
    const existing = await this.getEntryForDate(today);

    if (existing?.clockIn && !existing.clockOut) {
      this._todayEntry.set(existing);
      return;
    }

    if (existing) {
      await this.updateEntry(existing.id, {
        clockIn: now,
        clockOut: null,
        status: 'draft',
      });
      this._todayEntry.set({ ...existing, clockIn: now, clockOut: null, status: 'draft' });
      return;
    }

    const entryId = crypto.randomUUID();
    const entryRef = doc(
      this.firestore,
      `tenants/${this.tenantId}/attendanceEntries/${entryId}`
    );
    const newEntry: Omit<AttendanceEntry, 'id'> = {
      userId: this.userId,
      date: today,
      clockIn: now,
      clockOut: null,
      breakMinutes: 0,
      workedMinutes: 0,
      overtimeMinutes: 0,
      status: 'draft',
      notes: '',
      locationId: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(entryRef, {
      ...newEntry,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    this._todayEntry.set({ id: entryId, ...newEntry });
  }

  async clockOut(): Promise<void> {
    const now = new Date();
    const today = this.startOfDay(now);
    const existing = await this.getEntryForDate(today);

    if (!existing?.clockIn || existing.clockOut) {
      this._todayEntry.set(existing);
      return;
    }

    const workedMinutes = this.calculateWorkedMinutes(
      existing.clockIn,
      now,
      existing.breakMinutes
    );
    await this.updateEntry(existing.id, {
      clockOut: now,
      workedMinutes,
      status: 'draft',
    });
    this._todayEntry.set({
      ...existing,
      clockOut: now,
      workedMinutes,
      status: 'draft',
    });
  }

  async upsertEntryForDate(data: {
    date: Date;
    clockIn: Date | null;
    clockOut: Date | null;
    breakMinutes: number;
    notes: string;
  }): Promise<void> {
    const targetDate = this.startOfDay(data.date);
    const existing = await this.getEntryForDate(targetDate);
    const workedMinutes =
      data.clockIn && data.clockOut
        ? this.calculateWorkedMinutes(data.clockIn, data.clockOut, data.breakMinutes)
        : 0;

    if (existing) {
      if (existing.status === 'locked' || existing.status === 'approved') return;
      await this.updateEntry(existing.id, {
        clockIn: data.clockIn,
        clockOut: data.clockOut,
        breakMinutes: data.breakMinutes,
        notes: data.notes,
        workedMinutes,
        status: 'draft',
      });
      if (this.isSameDay(existing.date, targetDate)) {
        this._todayEntry.set({
          ...existing,
          clockIn: data.clockIn,
          clockOut: data.clockOut,
          breakMinutes: data.breakMinutes,
          notes: data.notes,
          workedMinutes,
          status: 'draft',
        });
      }
      return;
    }

    const entryId = crypto.randomUUID();
    const entryRef = doc(
      this.firestore,
      `tenants/${this.tenantId}/attendanceEntries/${entryId}`
    );
    const newEntry: Omit<AttendanceEntry, 'id'> = {
      userId: this.userId,
      date: targetDate,
      clockIn: data.clockIn,
      clockOut: data.clockOut,
      breakMinutes: data.breakMinutes,
      workedMinutes,
      overtimeMinutes: 0,
      status: 'draft',
      notes: data.notes,
      locationId: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(entryRef, {
      ...newEntry,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (this.isSameDay(targetDate, this.startOfDay(new Date()))) {
      this._todayEntry.set({ id: entryId, ...newEntry });
    }
  }

  async updateEntryNotes(entryId: string, data: { breakMinutes?: number; notes?: string }): Promise<void> {
    const entry = this._weekEntries().find((item) => item.id === entryId);
    if (!entry) return;
    if (entry.status === 'locked' || entry.status === 'approved') return;

    const breakMinutes = data.breakMinutes ?? entry.breakMinutes;
    const workedMinutes = entry.clockIn && entry.clockOut
      ? this.calculateWorkedMinutes(entry.clockIn, entry.clockOut, breakMinutes)
      : entry.workedMinutes;

    await this.updateEntry(entryId, {
      breakMinutes,
      notes: data.notes ?? entry.notes,
      workedMinutes,
    });

    this._weekEntries.update((entries) =>
      entries.map((item) =>
        item.id === entryId
          ? { ...item, breakMinutes, notes: data.notes ?? item.notes, workedMinutes }
          : item
      )
    );
  }

  async submitTimesheet(weekStart: Date): Promise<void> {
    const start = this.startOfDay(weekStart);
    const end = this.startOfDay(this.addDays(start, 6));
    const entries = this._weekEntries().filter(
      (entry) => entry.date >= start && entry.date <= end
    );

    const totalWorked = entries.reduce((sum, entry) => sum + (entry.workedMinutes ?? 0), 0);
    const totalOvertime = entries.reduce((sum, entry) => sum + (entry.overtimeMinutes ?? 0), 0);
    const approverEmployeeId = await this.getManagerEmployeeId();
    const existing = await this.getTimesheetByWeek(start);

    const timesheetData = {
      userId: this.userId,
      weekStartDate: start,
      weekEndDate: end,
      totalWorkedMinutes: totalWorked,
      totalOvertimeMinutes: totalOvertime,
      status: 'submitted' as TimesheetStatus,
      submittedAt: new Date(),
      approverId: null,
      approverEmployeeId: approverEmployeeId ?? null,
      approvedAt: null,
      rejectionReason: null,
      lockedAt: null,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const batch = writeBatch(this.firestore);
    if (existing) {
      const ref = doc(this.firestore, `tenants/${this.tenantId}/timesheets/${existing.id}`);
      batch.update(ref, {
        ...timesheetData,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      const id = crypto.randomUUID();
      const ref = doc(this.firestore, `tenants/${this.tenantId}/timesheets/${id}`);
      batch.set(ref, {
        ...timesheetData,
        createdAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    entries.forEach((entry) => {
      const entryRef = doc(
        this.firestore,
        `tenants/${this.tenantId}/attendanceEntries/${entry.id}`
      );
      batch.update(entryRef, {
        status: 'submitted',
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();
    await Promise.all([this.loadWeekEntries(start), this.loadMyTimesheets()]);
  }

  async approveTimesheet(timesheet: Timesheet): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/timesheets/${timesheet.id}`);
    await updateDoc(ref, {
      status: 'approved',
      approverId: this.userId,
      approvedAt: serverTimestamp(),
      lockedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await this.lockEntriesForWeek(timesheet.userId, timesheet.weekStartDate, timesheet.weekEndDate);
    await this.loadPendingApprovals();
  }

  async rejectTimesheet(timesheet: Timesheet, reason: string): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/timesheets/${timesheet.id}`);
    await updateDoc(ref, {
      status: 'rejected',
      approverId: this.userId,
      approvedAt: serverTimestamp(),
      rejectionReason: reason,
      updatedAt: serverTimestamp(),
    });

    await this.loadPendingApprovals();
  }

  async lockTimesheet(timesheet: Timesheet): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/timesheets/${timesheet.id}`);
    await updateDoc(ref, {
      status: 'locked',
      lockedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await this.lockEntriesForWeek(timesheet.userId, timesheet.weekStartDate, timesheet.weekEndDate);
  }

  private async lockEntriesForWeek(userId: string, start: Date, end: Date): Promise<void> {
    const ref = collection(this.firestore, `tenants/${this.tenantId}/attendanceEntries`);
    const q = query(
      ref,
      where('userId', '==', userId),
      where('date', '>=', start),
      where('date', '<=', end)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;

    const batch = writeBatch(this.firestore);
    snap.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, {
        status: 'locked',
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }

  private async getEntryForDate(date: Date): Promise<AttendanceEntry | null> {
    const ref = collection(this.firestore, `tenants/${this.tenantId}/attendanceEntries`);
    const q = query(ref, where('userId', '==', this.userId), where('date', '==', date));
    const snap = await getDocs(q);
    const docSnap = snap.docs[0];
    if (!docSnap) return null;
    return this.mapEntry(docSnap.id, docSnap.data());
  }

  private async updateEntry(
    entryId: string,
    data: Partial<AttendanceEntry>
  ): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/attendanceEntries/${entryId}`);
    await updateDoc(ref, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  private async getTimesheetByWeek(weekStart: Date): Promise<Timesheet | null> {
    const ref = collection(this.firestore, `tenants/${this.tenantId}/timesheets`);
    const q = query(ref, where('userId', '==', this.userId), where('weekStartDate', '==', weekStart));
    const snap = await getDocs(q);
    const docSnap = snap.docs[0];
    if (!docSnap) return null;
    return this.mapTimesheet(docSnap.id, docSnap.data());
  }

  private mapEntry(id: string, data: Record<string, unknown>): AttendanceEntry {
    return {
      id,
      userId: data['userId'] as string,
      date: (data['date'] as Timestamp)?.toDate() ?? new Date(),
      clockIn: (data['clockIn'] as Timestamp)?.toDate() ?? null,
      clockOut: (data['clockOut'] as Timestamp)?.toDate() ?? null,
      breakMinutes: (data['breakMinutes'] as number) ?? 0,
      workedMinutes: (data['workedMinutes'] as number) ?? 0,
      overtimeMinutes: (data['overtimeMinutes'] as number) ?? 0,
      status: (data['status'] as AttendanceEntryStatus) ?? 'draft',
      notes: (data['notes'] as string) ?? '',
      locationId: (data['locationId'] as string) ?? '',
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private mapTimesheet(id: string, data: Record<string, unknown>): Timesheet {
    return {
      id,
      userId: data['userId'] as string,
      weekStartDate: (data['weekStartDate'] as Timestamp)?.toDate() ?? new Date(),
      weekEndDate: (data['weekEndDate'] as Timestamp)?.toDate() ?? new Date(),
      totalWorkedMinutes: (data['totalWorkedMinutes'] as number) ?? 0,
      totalOvertimeMinutes: (data['totalOvertimeMinutes'] as number) ?? 0,
      status: (data['status'] as TimesheetStatus) ?? 'draft',
      submittedAt: (data['submittedAt'] as Timestamp)?.toDate() ?? null,
      approverId: (data['approverId'] as string) ?? null,
      approverEmployeeId: (data['approverEmployeeId'] as string) ?? null,
      approvedAt: (data['approvedAt'] as Timestamp)?.toDate() ?? null,
      rejectionReason: (data['rejectionReason'] as string) ?? null,
      lockedAt: (data['lockedAt'] as Timestamp)?.toDate() ?? null,
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private calculateWorkedMinutes(clockIn: Date, clockOut: Date, breakMinutes: number): number {
    const diffMs = Math.max(0, clockOut.getTime() - clockIn.getTime());
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    return Math.max(0, totalMinutes - breakMinutes);
  }

  private startOfDay(date: Date): Date {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    return value;
  }

  private addDays(date: Date, days: number): Date {
    const value = new Date(date);
    value.setDate(value.getDate() + days);
    return value;
  }

  private isSameDay(left: Date, right: Date): boolean {
    return (
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
    );
  }

  private async getEmployeeIdForUser(): Promise<string | null> {
    const employeesRef = collection(this.firestore, `tenants/${this.tenantId}/employees`);
    const primary = query(employeesRef, where('email', '==', this.userEmail));
    const snap = await getDocs(primary);
    const docSnap = snap.docs[0];
    if (docSnap) return docSnap.id;

    const fallback = query(employeesRef, where('email', '==', this.authStore.user()?.email ?? ''));
    const fallbackSnap = await getDocs(fallback);
    return fallbackSnap.docs[0]?.id ?? null;
  }

  private async getManagerEmployeeId(): Promise<string | null> {
    const employeeId = await this.getEmployeeIdForUser();
    if (!employeeId) return null;
    const ref = doc(this.firestore, `tenants/${this.tenantId}/employees/${employeeId}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    return (data['managerId'] as string | null) ?? null;
  }
}
